import React, { memo, useCallback, useState } from 'react';
import {
  ActionButton,
  Flex,
  Tooltip,
  TooltipTrigger
} from '@adobe/react-spectrum';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCollapseAll,
  selectCompact,
  selectUnNestAll
} from 'app/pages/Home/slice/selectors';
import { useHomeSlice } from 'app/pages/Home/slice';
import {
  exitCompactModeAndRestoreTimeline,
  getUserPreferences,
  saveUserPreferences
} from 'utils/helpers';
import { selectTimelineViewMode } from '../store/selectors';
import collapseAllIcon from 'images/collapse-all.svg';
import expandAllIcon from 'images/expand-all.svg';
import unNestAllIcon from 'images/un-nest-all.svg';
import nestAllIcon from 'images/nest-all.svg';
import { clearMonthlyManualExpandState } from '../utils/monthlyExpandUtils';
import styles from '../MonthlyRoadmap.module.scss';

export const TimelineControls = memo(() => {
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();
  const isCollapsed = useSelector(selectCollapseAll);
  const isCompact = useSelector(selectCompact);
  const isUnNested = useSelector(selectUnNestAll);
  const timelineViewMode = useSelector(selectTimelineViewMode);
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  const exitCompactIfNeeded = useCallback(() => {
    if (!isCompact) {
      return;
    }

    dispatch(actions.setCompact(false));
    exitCompactModeAndRestoreTimeline(timelineViewMode);
  }, [actions, dispatch, isCompact, timelineViewMode]);

  const handleTooltipMouseEnter = useCallback((tooltipId: string) => {
    setOpenTooltip(tooltipId);
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    setOpenTooltip(null);
  }, []);

  const onCollapseAll = useCallback(() => {
    dispatch(actions.setCollapseAll());

    const preferences = getUserPreferences();
    const newCollapseAllState = !isCollapsed;
    saveUserPreferences({
      ...preferences,
      collapseAll: newCollapseAllState,
      isUnNested: false
    });
  }, [dispatch, actions, isCollapsed]);

  const onToggleUnNest = useCallback(() => {
    exitCompactIfNeeded();

    const newUnNestedState = !isUnNested;
    dispatch(actions.setUnNestAll(newUnNestedState));
    clearMonthlyManualExpandState();

    const preferences = getUserPreferences();
    saveUserPreferences({
      ...preferences,
      isUnNested: newUnNestedState
    });
  }, [dispatch, actions, exitCompactIfNeeded, isUnNested]);

  return (
    <Flex
      data-testid="monthly-timeline-control-buttons"
      direction="row"
      gap="4px"
      UNSAFE_className={styles.timelineControls}
    >
      <TooltipTrigger
        isOpen={openTooltip === 'collapse-all'}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setOpenTooltip(null);
          }
        }}
        placement="top"
      >
        <div
          onMouseEnter={() => handleTooltipMouseEnter('collapse-all')}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <ActionButton
            data-testid="monthly-timeline-collapse-all-button"
            isQuiet
            isDisabled
            onPress={() => {
              exitCompactIfNeeded();
              onCollapseAll();
            }}
            UNSAFE_className={`${styles.timelineControlButton} ${styles.timelineControlButtonDisabled}`}
          >
            <img
              className={styles.timelineControlIcon}
              src={isCollapsed ? collapseAllIcon : expandAllIcon}
              alt={isCollapsed ? 'Collapse All' : 'Expand All'}
            />
          </ActionButton>
        </div>
        <Tooltip>{isCollapsed ? 'Large Cards' : 'Small Cards'}</Tooltip>
      </TooltipTrigger>

      <TooltipTrigger
        isOpen={openTooltip === 'unnest-all'}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setOpenTooltip(null);
          }
        }}
        placement="top"
      >
        <div
          onMouseEnter={() => handleTooltipMouseEnter('unnest-all')}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <ActionButton
            data-testid="monthly-timeline-unnest-all-button"
            isQuiet
            onPress={onToggleUnNest}
            UNSAFE_className={styles.timelineControlButton}
          >
            <img
              className={styles.timelineControlIcon}
              src={isUnNested ? nestAllIcon : unNestAllIcon}
              alt={isUnNested ? 'Un-Nest All' : 'Nest All'}
            />
          </ActionButton>
        </div>
        <Tooltip>{isUnNested ? 'Nest All' : 'Un-Nest All'}</Tooltip>
      </TooltipTrigger>
    </Flex>
  );
});
